import { formatDistanceToNow } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../../../components/ui/avatar";
import { Badge, type StatusType } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import { GroupBySelector, type GroupByField } from "../../../../components/ui/GroupBySelector";
import { KanbanBoard } from "../../../../components/ui/KanbanBoard";

type Props = {
  rows: any[];
  traits?: any;
  selectedColumns: string[];
  entityRelation?: string;
  total: number;
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  onPageIndexChange: (page: number) => void;
};

type BoardItem = {
  id: string;
  row: any;
};

const MAX_STATUS_COLUMNS = 8;
const LAST_SEEN_GROUPS = ["Active today", "Last 7 days", "Last 30 days", "Inactive"];

const getPaginationTokens = (currentPage: number, totalPages: number): Array<number | "ellipsis"> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const windowStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const windowPages = Array.from({ length: 5 }, (_, index) => windowStart + index)
    .filter((page) => page >= 1 && page <= totalPages);

  const tokens: Array<number | "ellipsis"> = [];
  if (windowPages[0] > 1) {
    tokens.push(1);
    if (windowPages[0] > 2) tokens.push("ellipsis");
  }

  windowPages.forEach((page) => tokens.push(page));

  const lastWindowPage = windowPages[windowPages.length - 1];
  if (lastWindowPage < totalPages) {
    if (lastWindowPage < totalPages - 1) tokens.push("ellipsis");
    tokens.push(totalPages);
  }

  return tokens;
};

const getFieldLabel = (fieldName: string, traits?: any) => (
  traits?.obj?.[fieldName]?.display_name
  || fieldName.split(".").pop()?.replace(/_/g, " ")
  || fieldName
);

const extractDomain = (value?: string) => {
  if (!value || typeof value !== "string") return "";
  return value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0];
};

const resolveStatusTone = (value: string): StatusType => {
  const normalized = value.trim().toLowerCase();
  if (/(active|subscribed|healthy|open|live)/.test(normalized)) return "active";
  if (/(trial|pending|in progress|processing)/.test(normalized)) return "pending";
  if (/(churn|error|failed|inactive|cancelled)/.test(normalized)) return "error";
  if (/(success|completed|won|done)/.test(normalized)) return "success";
  return "info";
};

const formatCardValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "-";

  if (Array.isArray(value)) {
    if (!value.length) return "-";
    const values = value
      .map((item) => formatCardValue(item))
      .filter((item) => item && item !== "-");
    if (!values.length) return "-";
    return values.slice(0, 3).join(", ");
  }

  if (typeof value === "object") {
    if (typeof value.name === "string" && value.name.trim()) return value.name;
    if (typeof value.externalId === "string" && value.externalId.trim()) return value.externalId;
    return "-";
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "number") {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "-";

    const parsedDate = new Date(trimmed.replace(" ", "T"));
    if (!Number.isNaN(parsedDate.getTime()) && trimmed.includes("-")) {
      try {
        return formatDistanceToNow(parsedDate, { addSuffix: true });
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  return String(value);
};

const toTextValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => toTextValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    return String(value?.name || value?.externalId || "");
  }
  return String(value).trim();
};

const hasMeaningfulValue = (value: any) => toTextValue(value).length > 0;

const resolveCardId = (row: any, index: number, isUserEntity: boolean) => (
  row?.randomId
  || row?.id
  || row?.[isUserEntity ? "users.external_id" : "accounts.external_id"]
  || `card-${index}`
);

const getInitials = (text: string) => text
  .split(" ")
  .filter(Boolean)
  .slice(0, 2)
  .map((item) => item[0]?.toUpperCase() || "")
  .join("");

const getLastSeenGroup = (rawValue: any): string => {
  const value = toTextValue(rawValue);
  if (!value) return "Inactive";

  const normalized = value.toLowerCase();
  if (normalized.includes("minute") || normalized.includes("hour") || normalized.includes("today")) {
    return "Active today";
  }
  if (normalized.includes("day")) {
    const dayMatch = normalized.match(/(\d+)\s*day/);
    if (dayMatch?.[1]) {
      const days = Number(dayMatch[1]);
      if (Number.isFinite(days) && days <= 7) return "Last 7 days";
      if (Number.isFinite(days) && days <= 30) return "Last 30 days";
      return "Inactive";
    }
  }

  const parsedDate = new Date(value.replace(" ", "T"));
  if (!Number.isNaN(parsedDate.getTime())) {
    const now = Date.now();
    const diffDays = Math.floor((now - parsedDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return "Active today";
    if (diffDays <= 7) return "Last 7 days";
    if (diffDays <= 30) return "Last 30 days";
  }

  return "Inactive";
};

const getStatusColumnColor = (status: string): string => {
  const normalized = status.trim().toLowerCase();
  if (/(subscribed|active|healthy|open|live)/.test(normalized)) return "hsl(142, 71%, 45%)";
  if (/(trial|pending|in progress|processing)/.test(normalized)) return "hsl(30, 88%, 62%)";
  if (/(churn|error|failed|inactive|cancelled)/.test(normalized)) return "hsl(1, 72%, 60%)";
  if (/(free|other|unknown)/.test(normalized)) return "hsl(212, 10%, 56%)";
  return "hsl(212, 10%, 56%)";
};

const lastSeenColumnColors: Record<string, string> = {
  "Active today": "hsl(142, 71%, 45%)",
  "Last 7 days": "hsl(30, 88%, 62%)",
  "Last 30 days": "hsl(258, 73%, 65%)",
  Inactive: "hsl(212, 10%, 56%)",
};

const AccountsKanbanView = ({
  rows,
  traits,
  selectedColumns,
  entityRelation,
  total,
  pageIndex,
  pageSize,
  pageCount,
  onPageIndexChange,
}: Props) => {
  const isUserEntity = entityRelation !== "accounts";
  const profileField = isUserEntity ? "users.name" : "accounts.name";

  const statusField = useMemo(() => {
    const preferred = [
      ...selectedColumns.filter((field) => /status/i.test(field)),
      isUserEntity ? "users.status" : "accounts.status",
      "status",
    ];

    const rowKeys = Object.keys(rows?.[0] || {});
    rowKeys.forEach((key) => {
      if (/status/i.test(key)) preferred.push(key);
    });

    const uniqueCandidates = Array.from(new Set(preferred.filter(Boolean)));
    return uniqueCandidates.find((candidate) => rows.some((row) => hasMeaningfulValue(row?.[candidate]))) || "";
  }, [rows, selectedColumns, isUserEntity]);

  const lastSeenField = useMemo(() => {
    const preferred = [
      ...selectedColumns.filter((field) => /(last_seen|last seen|last_activity|last activity)/i.test(field)),
      isUserEntity ? "users.last_seen" : "accounts.last_seen",
    ];

    const rowKeys = Object.keys(rows?.[0] || {});
    rowKeys.forEach((key) => {
      if (/(last_seen|last seen|last_activity|last activity)/i.test(key)) preferred.push(key);
    });

    const uniqueCandidates = Array.from(new Set(preferred.filter(Boolean)));
    return uniqueCandidates.find((candidate) => rows.some((row) => hasMeaningfulValue(row?.[candidate]))) || "";
  }, [rows, selectedColumns, isUserEntity]);

  const statusColumns = useMemo(() => {
    if (!statusField) return [];
    const values = Array.from(new Set(
      rows
        .map((row) => toTextValue(row?.[statusField]))
        .filter(Boolean),
    ));

    if (!values.length) return [];
    if (values.length <= MAX_STATUS_COLUMNS) return values;
    return [...values.slice(0, MAX_STATUS_COLUMNS - 1), "Other"];
  }, [rows, statusField]);

  const statusColumnSet = useMemo(() => new Set(statusColumns), [statusColumns]);

  const groupByFields = useMemo<GroupByField[]>(() => {
    const fields: GroupByField[] = [];
    if (statusColumns.length) {
      fields.push({
        key: "status",
        label: "Status",
        values: statusColumns,
      });
    }
    if (lastSeenField) {
      fields.push({
        key: "last_seen",
        label: "Last Seen",
        values: LAST_SEEN_GROUPS,
      });
    }
    if (!fields.length) {
      fields.push({
        key: "all",
        label: "All",
        values: ["All"],
      });
    }
    return fields;
  }, [statusColumns, lastSeenField]);

  const [groupBy, setGroupBy] = useState<string>("status");

  useEffect(() => {
    const fieldExists = groupByFields.some((field) => field.key === groupBy);
    if (!fieldExists) {
      setGroupBy(groupByFields[0]?.key || "all");
    }
  }, [groupByFields, groupBy]);

  const currentColumns = useMemo(
    () => groupByFields.find((field) => field.key === groupBy)?.values || [],
    [groupByFields, groupBy],
  );

  const kanbanItems = useMemo<BoardItem[]>(() => rows.map((row, index) => ({
    id: String(resolveCardId(row, index, isUserEntity)),
    row,
  })), [rows, isUserEntity]);

  const getGroupValue = useCallback((item: BoardItem) => {
    if (groupBy === "status") {
      const value = toTextValue(item?.row?.[statusField]) || "Other";
      if (!statusColumnSet.size) return "Other";
      return statusColumnSet.has(value) ? value : "Other";
    }
    if (groupBy === "last_seen") {
      return getLastSeenGroup(item?.row?.[lastSeenField]);
    }
    return "All";
  }, [groupBy, lastSeenField, statusField, statusColumnSet]);

  const columnColors = useMemo(() => {
    if (groupBy === "status") {
      return currentColumns.reduce((acc: Record<string, string>, column) => {
        acc[column] = getStatusColumnColor(column);
        return acc;
      }, {});
    }
    if (groupBy === "last_seen") return lastSeenColumnColors;
    return undefined;
  }, [groupBy, currentColumns]);

  const cardFields = useMemo(() => {
    const hiddenFields = new Set([
      profileField,
      statusField,
      lastSeenField,
      isUserEntity ? "users.external_id" : "accounts.external_id",
      isUserEntity ? "users.avatar_url" : "accounts.avatar_url",
      "related_accounts",
    ]);

    return (selectedColumns || [])
      .filter((field) => !hiddenFields.has(field))
      .filter((field) => !traits?.obj?.[field]?.exclude_display)
      .slice(0, 4);
  }, [selectedColumns, traits?.obj, profileField, statusField, lastSeenField, isUserEntity]);

  const currentPage = pageIndex + 1;
  const safeTotalPages = Math.max(1, pageCount || 1);
  const rangeStart = total > 0 ? (pageIndex * pageSize) + 1 : 0;
  const rangeEnd = total > 0 ? Math.min(total, (pageIndex * pageSize) + rows.length) : 0;
  const paginationTokens = useMemo(
    () => getPaginationTokens(currentPage, safeTotalPages),
    [currentPage, safeTotalPages],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-2">
        <GroupBySelector
          fields={groupByFields}
          selectedField={groupBy}
          onFieldChange={setGroupBy}
        />
      </div>

      <KanbanBoard<BoardItem>
        items={kanbanItems}
        groupByField={groupBy}
        getGroupValue={getGroupValue}
        columns={currentColumns}
        columnColors={columnColors}
        renderCard={(item) => {
          const row = item.row;
          const profile = row?.[profileField] || {};
          const name = profile?.name || "Unknown";
          const subtitle = profile?.desc || profile?.externalId || "No additional details";
          const statusValue = statusField ? toTextValue(row?.[statusField]) : "";
          const fallbackDomain = getDomainFromCompany(name);
          const logoDomain = extractDomain(profile?.desc) || fallbackDomain;

          return (
            <article className="rounded-lg border border-border-strong bg-card p-3.5 transition-colors hover:border-border-chip">
              <div className="flex items-start gap-3">
                {isUserEntity ? (
                  <Avatar className="h-8 w-8 rounded-md">
                    <AvatarImage src={profile?.img || ""} alt={name} />
                    <AvatarFallback className="rounded-md text-[11px] font-semibold text-foreground">
                      {getInitials(name) || "U"}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <CompanyLogo
                    domain={logoDomain}
                    name={name}
                    className="h-8 w-8 rounded-md"
                  />
                )}

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{name}</p>
                  <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                </div>
              </div>

              {!!statusValue && (
                <div className="mt-3">
                  <Badge variant="statusPill" status={resolveStatusTone(statusValue)} className="text-[11px]">
                    {statusValue}
                  </Badge>
                </div>
              )}

              {!!cardFields.length && (
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                  {cardFields.map((field) => (
                    <div key={field} className="min-w-0">
                      <p className="truncate text-[11px] text-muted-foreground">
                        {getFieldLabel(field, traits)}
                      </p>
                      <p className="truncate text-sm font-medium text-foreground">
                        {formatCardValue(row?.[field])}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        }}
        onDragEnd={() => {}}
        showEmptyColumns
      />

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <span className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{rangeStart}-{rangeEnd}</span>
            {" "}
            of
            {" "}
            <span className="font-medium text-foreground">{total}</span>
            {" "}
            {isUserEntity ? "Contacts" : "Accounts"}
          </span>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageIndexChange(pageIndex - 1)}
              disabled={pageIndex <= 0}
              className="gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            {paginationTokens.map((token, tokenIndex) => (
              token === "ellipsis" ? (
                <span key={`ellipsis-${tokenIndex}`} className="px-1.5 text-sm text-muted-foreground">...</span>
              ) : (
                <Button
                  key={`page-${token}`}
                  variant={token === currentPage ? "default" : "ghost"}
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onPageIndexChange(token - 1)}
                >
                  {token}
                </Button>
              )
            ))}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageIndexChange(pageIndex + 1)}
              disabled={pageIndex >= safeTotalPages - 1}
              className="gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsKanbanView;
